import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { roleIcons } from '../../mocks/stocks';
import OfflineIndicator from '../../components/base/OfflineIndicator';
import { api } from '../../services/api';
import { useBranding } from '../../context/BrandingContext';

const roleRedirects: Record<string, string> = {
  superadmin: '/dashboard/superadmin',
  gerant: '/dashboard',
  vendeur: '/dashboard',
  coupeur: '/dashboard',
  tailleur: '/dashboard',
  responsable_atelier: '/dashboard',
};

const roleLabels: Record<string, string> = {
    superadmin: 'Super Admin',
    gerant: 'Gérant',
    vendeur: 'Vendeur(se)',
    coupeur: 'Coupeur',
    tailleur: 'Tailleur',
    responsable_atelier: 'Responsable Atelier',
};

export default function LoginPage() {
  const navigate = useNavigate();
  const { branding } = useBranding();
  const [selectedRole, setSelectedRole] = useState('');
  const [selectedUser, setSelectedUser] = useState('');
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<any[]>([]);

  useEffect(() => {
    api.getPublicUsers()
      .then(data => setUsers(data))
      .catch(err => {
        console.error("Erreur chargement utilisateurs:", err);
        setError("Impossible de charger la liste des utilisateurs.");
      });
  }, []);

  const roles = [
    { value: 'superadmin', label: 'Super Admin' },
    { value: 'vendeur', label: 'Vendeur(se)' },
    { value: 'responsable_atelier', label: 'Responsable Atelier' },
    { value: 'gerant', label: 'Gérant' },
  ];

  const usersForRole = users.filter(u => !selectedRole || u.role === selectedRole);

  const handlePinInput = (digit: string) => {
    if (pin.length < 4) {
      setPin(prev => prev + digit);
      setError('');
    }
  };

  const handlePinDelete = () => {
    setPin(prev => prev.slice(0, -1));
    setError('');
  };

  const handleLogin = async () => {
    if (!selectedUser || pin.length < 4) {
      setError('Veuillez sélectionner un utilisateur et saisir votre code PIN.');
      return;
    }
    const user = users.find(u => u.id === selectedUser);
    if (!user) { setError('Utilisateur introuvable.'); return; }
    
    setLoading(true);
    try {
      const result = await api.login(user.email, pin, user.id);
      localStorage.setItem('atelier_bassoum_token', result.token);
      localStorage.setItem('atelier_bassoum_user', JSON.stringify(result.user));
      
      setTimeout(() => {
        navigate(roleRedirects[result.user.role] || '/dashboard');
      }, 700);
    } catch (err: any) {
      setError(err.message || 'Erreur de connexion.');
      setPin('');
    } finally {
      setLoading(false);
    }
  };

  const handleDemoLogin = async (role: string) => {
    const user = users.find(u => u.role === role);
    if (!user) return;
    
    const pins: Record<string, string> = {
      superadmin: '0000',
      gerant: '1234',
      vendeur: '2345',
      responsable_atelier: '5678'
    };

    setLoading(true);
    try {
      const result = await api.login(user.email, pins[role] || '0000', user.id);
      localStorage.setItem('atelier_bassoum_token', result.token);
      localStorage.setItem('atelier_bassoum_user', JSON.stringify(result.user));
      
      setTimeout(() => {
        navigate(role === 'superadmin' ? '/dashboard/superadmin' : '/dashboard');
      }, 500);
    } catch (err: any) {
      setError("Erreur : " + (err.message || 'Connexion impossible.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-terre-800 via-terre-600 to-foret-700 relative overflow-hidden font-nunito flex flex-col items-center justify-center">
      <OfflineIndicator />

      {/* Background pattern */}
      <div className="absolute inset-0 opacity-10 pointer-events-none">
        <img
          src="https://readdy.ai/api/search-image?query=African%20wax%20fabric%20pattern%20abstract%20geometric%20colorful%20batik%20ankara%20texture%20background%20repeat%20pattern%20Mali%20Bamako%20traditional%20textile&width=1200&height=900&seq=bg001&orientation=landscape"
          alt=""
          className="w-full h-full object-cover"
        />
      </div>

      <div className="relative z-10 w-full max-w-6xl flex flex-col lg:flex-row items-center justify-center p-4">
        {/* Left brand panel (desktop only) */}
        <div className="hidden lg:flex flex-col justify-center w-1/2 p-12 text-white">
          <div className="max-w-md">
            <div className="w-20 h-20 rounded-2xl bg-or-400 flex items-center justify-center mb-6 shadow-xl overflow-hidden">
               {branding.logoUrl ? (
                <img src={api.getProxyUrl(branding.logoUrl)} alt="Logo" className="w-full h-full object-cover" />
              ) : (
                <i className="ri-scissors-2-line text-foret-700 text-4xl" />
              )}
            </div>
            <h1 className="font-playfair text-5xl font-bold mb-4 leading-tight">{branding.nom}</h1>
            <p className="text-terre-100 text-xl mb-1">{branding.slogan || 'Gestion intégrée'}</p>
            <p className="text-or-300 text-lg font-semibold mb-8">Bamako, Mali</p>
            <div className="grid grid-cols-2 gap-4 text-left">
              {[
                { icon: 'ri-store-2-line', text: 'Ventes' },
                { icon: 'ri-refresh-line', text: 'Production' },
                { icon: 'ri-stack-line', text: 'Stocks' },
                { icon: 'ri-team-line', text: 'Équipe' },
              ].map(item => (
                <div key={item.text} className="flex items-center gap-3 bg-white/5 rounded-xl px-4 py-3 border border-white/10 hover:bg-white/10 transition-colors">
                  <i className={`${item.icon} text-or-300`} />
                  <span className="text-sm text-white/90">{item.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right login panel (mobile priority) */}
        <div className="w-full lg:w-1/2 max-w-md">
          {/* Mobile header view */}
          <div className="lg:hidden text-center mb-6">
            <div className="w-14 h-14 rounded-xl bg-or-400 flex items-center justify-center mx-auto mb-2 shadow-lg overflow-hidden">
              {branding.logoUrl ? (
                <img src={api.getProxyUrl(branding.logoUrl)} alt="Logo" className="w-full h-full object-cover" />
              ) : (
                <i className="ri-scissors-2-line text-foret-700 text-2xl" />
              )}
            </div>
            <h1 className="font-playfair text-2xl font-bold text-white">{branding.nom}</h1>
            <p className="text-terre-100 text-xs opacity-80">{branding.adresse || 'Bamako, Mali'}</p>
          </div>

          <div className="bg-white rounded-2xl shadow-3xl overflow-hidden">
            <div className="bg-foret-600 px-6 py-4">
              <h2 className="text-white font-playfair text-lg font-bold">Connexion</h2>
              <p className="text-foret-200 text-xs mt-0.5">Identifiez-vous pour accéder au système</p>
            </div>

            <div className="p-6">
              {/* Role select */}
              <div className="mb-4">
                <label htmlFor="login-role-select" className="block text-[10px] font-bold text-gray-500 mb-1.5 uppercase tracking-widest">Type d'accès</label>
                <div className="relative">
                  <select
                    id="login-role-select"
                    value={selectedRole}
                    onChange={e => { setSelectedRole(e.target.value); setSelectedUser(''); setPin(''); }}
                    className="w-full appearance-none bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 text-sm text-gray-800 focus:ring-2 focus:ring-foret-500 outline-none transition-all cursor-pointer"
                  >
                    <option value="">Tous les types</option>
                    {roles.map(r => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                  </select>
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
                    <i className="ri-arrow-down-s-line" />
                  </div>
                </div>
              </div>

              {/* User select list */}
              <div className="mb-5">
                <label className="block text-[10px] font-bold text-gray-500 mb-1.5 uppercase tracking-widest">Choisir un profil</label>
                <div className="grid gap-2 max-h-40 overflow-y-auto pr-1 scrollbar-thin scrollbar-thumb-gray-200">
                  {usersForRole.length === 0 ? (
                      <p className="text-center py-4 text-xs text-gray-400 italic">Aucun utilisateur trouvé</p>
                  ) : usersForRole.map(user => (
                    <button
                      key={user.id}
                      onClick={() => { setSelectedUser(user.id); setPin(''); setError(''); }}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border text-left transition-all cursor-pointer ${
                        selectedUser === user.id ? 'border-foret-500 bg-foret-50' : 'border-gray-100 hover:bg-gray-50'
                      }`}
                    >
                      <div className={`w-9 h-9 rounded-full flex items-center justify-center font-bold text-xs flex-shrink-0 ${
                        selectedUser === user.id ? 'bg-foret-600 text-white' : 'bg-terre-100 text-terre-700'
                      }`}>
                        {user.nom.charAt(0).toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-800 truncate">{user.nom}</p>
                        <span className="text-[10px] text-gray-400">{roleLabels[user.role]}</span>
                      </div>
                      {selectedUser === user.id && (
                        <i className="ri-checkbox-circle-fill ml-auto text-foret-600" />
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* PIN Keypad */}
              {selectedUser && (
                <div className="mb-5 animate-in fade-in slide-in-from-top-2 duration-300">
                   <div className="flex gap-2.5 justify-center mb-4">
                    {[0,1,2,3].map(i => (
                      <div key={i} className={`w-10 h-10 rounded-xl border flex items-center justify-center transition-all ${
                        i < pin.length ? 'border-foret-500 bg-foret-50 text-foret-600 font-bold' : 'border-gray-200 bg-gray-50'
                      }`}>
                        {i < pin.length ? '●' : ''}
                      </div>
                    ))}
                  </div>
                  <div className="grid grid-cols-3 gap-2 max-w-[260px] mx-auto">
                    {['1','2','3','4','5','6','7','8','9','0'].map(d => (
                      d === '0' ? (
                        <React.Fragment key="pad-footer">
                          <div />
                          <button key="0" onClick={() => handlePinInput('0')} className="py-2.5 text-lg font-bold bg-gray-50 hover:bg-gray-100 rounded-xl active:scale-95 transition-all text-gray-800 cursor-pointer">0</button>
                          <button key="del" onClick={handlePinDelete} title="Supprimer" className="py-2.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-xl active:scale-95 transition-all cursor-pointer"><i className="ri-delete-back-2-line text-lg" /></button>
                        </React.Fragment>
                      ) : d !== '0' && (
                        <button key={d} onClick={() => handlePinInput(d)} className="py-2.5 text-lg font-bold bg-gray-50 hover:bg-gray-100 rounded-xl active:scale-95 transition-all text-gray-800 cursor-pointer">{d}</button>
                      )
                    ))}
                  </div>
                </div>
              )}

              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-100 text-red-600 rounded-xl text-xs flex items-center gap-2">
                  <i className="ri-error-warning-line text-sm" />
                  {error}
                </div>
              )}

              <button
                onClick={handleLogin}
                disabled={loading || pin.length < 4 || !selectedUser}
                className="w-full py-3.5 bg-foret-600 hover:bg-foret-700 text-white rounded-xl font-bold shadow-lg shadow-foret-200 disabled:opacity-50 disabled:shadow-none transition-all cursor-pointer flex items-center justify-center gap-2"
              >
                {loading ? <i className="ri-loader-4-line animate-spin text-lg" /> : <i className="ri-login-box-line text-lg" />}
                {loading ? 'Connexion...' : 'Se connecter'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

